import { useEffect, useState } from 'react';
import { useAirKeyTrace } from '@/utils/airKeyTrace';
import capitalOneLogo from '@assets/C1.svg';
import { UiArrowUpLined } from '@cof/omni-gravity-icons-react';
import { Icon, Spinner } from '@cof/omni-react';
import { colorBlue45 } from '@cof/omni-tokens';
import { ErrorScreen_DEPRECIATED } from '@components/ErrorScreen';
import { WarningBox } from '@components/global/Box';
import { CopyUrlButton } from '@components/global/CopyUrlButton';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { useBowserParser } from '@hooks/useBowserParser';

const OpenPrompt = styled('div')`
  text-align: center;
  font-weight: 700;
  font-size: 1em;
`;

const bounce = keyframes`
  0%, 100% {
    bottom: 10px;
    animation-timing-function: ease-in;
  }
  30% {
    bottom: 20px;
    animation-timing-function: ease-out;
  }
`;

const PromptArrow = styled(Icon)`
  margin-top: 15px;
  margin-left: 5px;
  animation: ${bounce} 1s infinite alternate;
`;

const LowerHalf = styled('div')`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
`;

const OpenSpan = styled('span')`
  color: white;
  background-color: ${colorBlue45};
  padding: 4px 10px 7px;
  border-radius: 20px;
`;

const AlertHeader = styled('h3')`
  margin: 12px 0 0;
  font-weight: 700;
  font-size: 1em;
`;

export const ReloadPrompt = ({
  promptTime = 30000,
}: {
  promptTime?: number;
}) => {
  const [isStuck, setIsStuck] = useState(false);
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsStuck(true);
    }, promptTime);
    return () => {
      clearTimeout(timeout);
    };
  }, [promptTime]);

  useEffect(() => {
    if (isStuck) {
      sendTrace({
        view: 'ios_screen',
        action: 'timed_out',
      });
    }
  }, [isStuck]);

  return (
    <div
      data-testid="IPhoneScreen-ReloadPrompt"
      style={{
        width: '100%',
        minHeight: '300px',
        padding: '0 20px',
      }}
    >
      {isStuck ? (
        <WarningBox data-testid="IPhoneScreen-ReloadPrompt--prompt">
          <div>
            <AlertHeader>This is taking longer than usual</AlertHeader>
            <p>
              If you don&apos;t see the &quot;open&quot; button on the top of
              this screen, please try closing and reopening this page.
            </p>
          </div>
        </WarningBox>
      ) : null}
    </div>
  );
};

const NotSafari = () => {
  const sendTrace = useAirKeyTrace();

  useEffect(() => {
    sendTrace({
      view: 'safari_required',
      action: 'view_loaded',
    });
  }, []);

  return (
    <div
      data-testid="IPhoneScreen-NotSafari"
      style={{
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        minHeight: '300px',
        padding: '40% 20px',
        textAlign: 'center',
        width: '100%',
      }}
    >
      <img
        src={capitalOneLogo}
        width="65%"
        alt="Capital One"
        role="presentation"
      />
      <h3 style={{ paddingTop: '20px' }}>This browser is not supported</h3>
      <CopyUrlButton message="Please copy this link into Safari to continue:" />
    </div>
  );
};

export const IPhoneScreen = () => {
  const { isPrivateMode, isSafari, isValidIOSVersion, loading } =
    useBowserParser();
  const incompatibleDeviceBody = `The device you are using does not support AirKey taps.
    Return to the merchant and choose a different form of identity verification if available,
    or try using another device for the AirKey tap.`;
  const incompatibleBrowserMode =
    "To continue, you'll need to turn off private browsing mode on this device.";

  const [actionSent, setActionSent] = useState(false);
  const sendTrace = useAirKeyTrace();

  if (loading) {
    return (
      <div className="brand" data-testid="IPhoneScreen-Loader">
        <img
          src={capitalOneLogo}
          height="86"
          alt="Capital One"
          role="presentation"
        />
        <Spinner size="giant" />
      </div>
    );
  }

  if (isValidIOSVersion && isSafari && !isPrivateMode) {
    if (!actionSent) {
      sendTrace({
        view: 'ios_screen',
        action: 'view_loaded',
      });
      setActionSent(true);
    }
    return (
      <>
        <div className="brand" data-testid="IPhoneScreen">
          <img
            src={capitalOneLogo}
            height="86"
            alt="Capital One"
            role="presentation"
          />
          <LowerHalf>
            <OpenPrompt>
              Tap <OpenSpan>open</OpenSpan> on the top of this screen
              <PromptArrow svg={UiArrowUpLined} color="interactive" />
            </OpenPrompt>
            <ReloadPrompt />
          </LowerHalf>
        </div>
      </>
    );
  }

  if (!isValidIOSVersion) {
    return (
      <ErrorScreen_DEPRECIATED
        title="Incompatible Device"
        body={incompatibleDeviceBody}
        action="NotSupported"
      />
    );
  }

  if (!isSafari) {
    return <NotSafari />;
  }

  if (isPrivateMode) {
    return (
      <>
        <ErrorScreen_DEPRECIATED
          title="Turn off private browsing"
          body={incompatibleBrowserMode}
          action="PrivateTabSafari"
          helper={
            <>
              <a href="https://support.apple.com/en-us/105030">
                Here's how to do it.
              </a>
            </>
          }
        />
      </>
    );
  }

  // Fallback case (should not be reached)
  return null;
};
